import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCity } from '../context/CityContext';
import api, { notificationService, chatService } from '../api';
import { signalRService } from '../signalRService';
import DealzyLogo from '../assets/DealzyLogo';
import NavMenu from './NavMenu';
import AuthModal from './auth/AuthModal';
import MobileTabBar from './MobileTabBar';
import './Navbar.css';

const BellIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

const MessageIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </svg>
);

const HeartIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.5 4.04 3 5.5l7 7Z" />
    </svg>
);

const BurgerIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
);

const HeaderIconButton = ({ onClick, label, hasUnread, children }) => (
    <button
        type="button"
        className="site-header__icon-btn"
        onClick={onClick}
        aria-label={label}
        title={label}
    >
        {children}
        {hasUnread && <span className="site-header__icon-badge" />}
    </button>
);

const CityChip = ({ cityName, onReset }) => (
    <div className="site-header__city">
        <span className="site-header__city-name">{cityName}</span>
        <button
            type="button"
            className="site-header__city-reset"
            onClick={onReset}
            aria-label="Сбросить город"
            title="Сбросить город"
        >
            ×
        </button>
    </div>
);

const Navbar = () => {
    const { user, isAdmin } = useAuth();
    const { cityName, clearCityName } = useCity();
    const [authOpen, setAuthOpen] = useState(false);
    const [notifUnread, setNotifUnread] = useState(0);
    const [msgUnread, setMsgUnread] = useState(0);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Hide nav-menu on profile, ad edit/create, and admin pages
    const hideNavMenu = ['/profile', '/create', '/edit', '/admin'].some(
        (p) => location.pathname.startsWith(p)
    );

    // Hide bottom tab bar on full-screen ad wizard pages
    const hideTabBar = ['/create', '/edit'].some((p) => location.pathname.startsWith(p));

    const refreshUnreadCounts = useCallback(() => {
        if (!user) return;
        notificationService.getUnreadCount()
            .then(({ data }) => setNotifUnread(data.count))
            .catch(() => {});
        chatService.getUnreadCount()
            .then(({ data }) => setMsgUnread(data.count))
            .catch(() => {});
    }, [user]);

    // Poll periodically while logged in
    useEffect(() => {
        if (!user) {
            setNotifUnread(0);
            setMsgUnread(0);
            return;
        }
        refreshUnreadCounts();
        const interval = setInterval(refreshUnreadCounts, 30000);
        return () => clearInterval(interval);
    }, [user, refreshUnreadCounts]);

    // Re-check on navigation, e.g. after the user reads notifications/messages on the profile page
    useEffect(() => {
        refreshUnreadCounts();
    }, [location.pathname, refreshUnreadCounts]);

    // Fetch avatar for header once logged in
    useEffect(() => {
        if (!user) {
            setAvatarUrl('');
            return;
        }
        api.get('/profile')
            .then(({ data }) => setAvatarUrl(data.avatarUrl || ''))
            .catch(() => {});
    }, [user]);

    // Live update on incoming chat messages
    useEffect(() => {
        if (!user) return;
        signalRService.start().catch(() => {});
        const handler = (msg) => {
            if (msg.senderId !== user.id) refreshUnreadCounts();
        };
        signalRService.on('ReceiveMessage', handler);
        return () => signalRService.off('ReceiveMessage', handler);
    }, [user, refreshUnreadCounts]);

    const goToTab = (tab) => {
        navigate('/profile', { state: { tab } });
    };

    const handleCreateAd = () => {
        if (user) {
            navigate('/create');
        } else {
            setAuthOpen(true);
        }
    };

    return (
        <>
            <header className="site-header">
                <div className="site-header__top">
                    <div className="site-header__top-inner">
                        <Link to="/" className="site-header__logo">
                            <DealzyLogo width={48} height={48} className="site-header__logo-mark" />
                            <span className="site-header__logo-text">DEALZY</span>
                        </Link>

                        <div className="site-header__actions">
                            {!hideNavMenu && (
                                <button
                                    type="button"
                                    className="site-header__icon-btn site-header__burger"
                                    onClick={() => setMobileMenuOpen(true)}
                                    aria-label="Меню категорий"
                                >
                                    <BurgerIcon />
                                </button>
                            )}
                            <button onClick={handleCreateAd} className="site-header__btn site-header__btn--outline site-header__btn--create">
                                Создать объявление
                            </button>
                            {cityName && <CityChip cityName={cityName} onReset={clearCityName} />}
                            {user ? (
                                <>
                                    <div className="site-header__secondary-icons">
                                        <HeaderIconButton
                                            label="Уведомления"
                                            hasUnread={notifUnread > 0}
                                            onClick={() => goToTab('notifications')}
                                        >
                                            <BellIcon />
                                        </HeaderIconButton>
                                        <HeaderIconButton
                                            label="Сообщения"
                                            hasUnread={msgUnread > 0}
                                            onClick={() => goToTab('messages')}
                                        >
                                            <MessageIcon />
                                        </HeaderIconButton>
                                        <HeaderIconButton
                                            label="Избранные"
                                            hasUnread={false}
                                            onClick={() => goToTab('favorites')}
                                        >
                                            <HeartIcon />
                                        </HeaderIconButton>
                                    </div>

                                    {isAdmin && (
                                        <Link
                                            to="/admin/reports"
                                            className="site-header__btn site-header__btn--ghost"
                                        >
                                            Админка
                                        </Link>
                                    )}
                                    <Link
                                        to="/profile"
                                        className="site-header__profile-link"
                                    >
                                        <span className="site-header__avatar">
                                            {avatarUrl ? (
                                                <img src={avatarUrl} alt="" className="site-header__avatar-img" />
                                            ) : (
                                                user.name?.charAt(0).toUpperCase()
                                            )}
                                        </span>
                                        <span className="site-header__profile-name">{user.name}</span>
                                    </Link>
                                </>
                            ) : (
                                <button
                                    className="site-header__btn site-header__btn--filled"
                                    onClick={() => setAuthOpen(true)}
                                >
                                    Войти
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {!hideNavMenu && <NavMenu />}
                {!hideNavMenu && mobileMenuOpen && (
                    <NavMenu mobileOpen onCloseMobile={() => setMobileMenuOpen(false)} />
                )}
            </header>

            <AuthModal
                isOpen={authOpen}
                onClose={() => setAuthOpen(false)}
            />

            {!hideTabBar && (
                <MobileTabBar
                    user={user}
                    msgUnread={msgUnread}
                    onRequireAuth={() => setAuthOpen(true)}
                    onCreateAd={handleCreateAd}
                    onGoToTab={goToTab}
                />
            )}
        </>
    );
};

export default Navbar;
